import { computeTimeRange } from 'src/app/components/Models/DeliveryWindow/container';
import type { DeliveryAddress, DeliveryMethod, PaymentMethod } from 'src/app/types';
import { isNoneString } from 'src/app/utils/stringUtils';
import { DeliveryMethodType } from 'src/consts';

type UIText = { firstRowText: string; secondRowText: string };

export const getUITextForDeliveryMethod = (
  deliveryMethod: DeliveryMethod,
  hasChosenDeliveryAddressButNotDeliveryMethod: boolean
): UIText => {
  if (hasChosenDeliveryAddressButNotDeliveryMethod)
    return { firstRowText: 'Choose delivery time', secondRowText: null };

  const { key, name, deliveryWindow, shippingOption, flavorText } = deliveryMethod || {};
  if (key === DeliveryMethodType.courier) {
    const [day, formattedTimeRange] = computeTimeRange(deliveryWindow, true);
    return { firstRowText: name, secondRowText: `${day} ${formattedTimeRange}` };
  } else if (key === DeliveryMethodType.mail) {
    return { firstRowText: name, secondRowText: shippingOption?.description };
  } else if (key === DeliveryMethodType.pickupStation) {
    const flavorTextSentences = flavorText.split('.');
    const closingTimeFlavorText = flavorTextSentences[1];
    return {
      firstRowText: `${flavorTextSentences[0]}.`,
      secondRowText: closingTimeFlavorText ? `${closingTimeFlavorText}.` : null,
    };
  }
  return { firstRowText: null, secondRowText: null };
};

export const getUITextForDeliveryAddress = (
  deliveryAddress: DeliveryAddress,
  defaultFirstRowText = 'Choose delivery address'
): UIText => {
  const { address, addressTwo, city, state, zipCode } = deliveryAddress || {};
  return {
    firstRowText: deliveryAddress ? `${address} ${addressTwo ? addressTwo : ''}` : defaultFirstRowText,
    secondRowText: deliveryAddress ? `${city}, ${state} ${zipCode}` : null,
  };
};

export const getUITextForPaymentMethod = (
  paymentMethod: PaymentMethod,
  hasDeliveryAddressAndDeliveryMethodBeenSet
): UIText => {
  if (!paymentMethod && hasDeliveryAddressAndDeliveryMethodBeenSet)
    return {
      firstRowText: 'Add credit card',
      secondRowText: null,
    };
  const { brand, last4, expirationDate } = paymentMethod || {};
  return {
    firstRowText: `${brand} ${last4}`,
    secondRowText: `Expires ${expirationDate}`,
  };
};

export const MEDICATIONS_DELIMITER = ' | ';
const NO_RELEVANT_VALUES = [''];

/**
 * Medications and doctors (for MCYP experiment) are stored as delimited strings. This utility turns that delimited
 * string into an array of each value.
 */
export const splitMedications = (medicationsString: string | null): string[] => {
  if (medicationsString) {
    const splitMedications = medicationsString.split(MEDICATIONS_DELIMITER);
    const validMeds = splitMedications.filter(medication => !isNoneString(medication));

    return validMeds.length > 0 ? validMeds : NO_RELEVANT_VALUES;
  }

  return NO_RELEVANT_VALUES;
};

// If there is no doctor, the backend stores it and receives it as `"null"` 🤷‍♂️
export const NULL_DOCTOR_VALUE = 'null';

export const convertNumberToValueInCents = (amount?: number) =>
  amount !== null && amount !== undefined ? amount * 100 : null;
